// CPLEX Admin Page — model registry + run dispatch
//
// Routes API: /api/admin/cplex/* (see app/routes/admin_cplex.py).
// Three tabs: Models | Run | History. Polling for active run state.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Tab, TabList, TabPanel, Tabs } from '../components/common/Tabs';
import {
  SankeyChart,
  type SankeyPayload,
} from '../components/cplex/SankeyChart';
import { DailyDashboard } from '../components/cplex/dailyView/DailyDashboard';
import type { MPPRow } from '../components/cplex/dailyView/types';
import {
  archiveModel,
  createModel,
  downloadRunArchive,
  getModel,
  getNotebookUploadGuide,
  getRun,
  getWorkspaceDownloadUrl,
  getWorkspaceFile,
  listModels,
  listRuns,
  startRun,
  TERMINAL_STATES,
  updateModel,
  uploadNotebookVersion,
  uploadVersion,
  type CplexModel,
  type CplexModelDetail,
  type CplexModelVersion,
  type CplexRun,
  type CplexRunSummary,
  type WorkspaceFileContent,
} from '../services/cplex';
import { CplexSourcesPanel } from './CplexSourcesPanel';
import {
  formatBytes,
  formatTime,
  isTerminalState,
  stateLabel,
} from './CplexAdminPage.helpers';
import styles from './CplexAdminPage.module.css';

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function CplexAdminPage() {
  const [activeTab, setActiveTab] = useState('models');

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>CPLEX Model Runner</h1>
          <p className={styles.lede}>
            Manage registered optimization models. Solves run on the
            dedicated CPLEX VM, reached via{' '}
            <code>api.chemmasters.net/cplex</code>.
          </p>
        </div>
      </div>

      <ExampleUsage />

      <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
        <TabList>
          <Tab value="models">Models</Tab>
          <Tab value="run">Run</Tab>
          <Tab value="history">History</Tab>
          <Tab value="sankey">Sankey</Tab>
        </TabList>
        <TabPanel value="models">
          <ModelsPanel />
        </TabPanel>
        <TabPanel value="run">
          <RunPanel />
        </TabPanel>
        <TabPanel value="history">
          <HistoryPanel />
        </TabPanel>
        <TabPanel value="sankey">
          <SankeyPanel />
        </TabPanel>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Example usage — collapsible help block
// ---------------------------------------------------------------------------

function ExampleUsage() {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.section}>
      <button
        className={`${styles.btn} ${styles.secondary} ${styles.small}`}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? '▾ Hide' : '▸ Show'} example usage
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          <p className={styles.muted}>
            A <strong>model</strong> is a workspace of files. Upload whatever you need
            — Python files, CSVs, JSON, sub-directories, anything. The CPLEX VM
            extracts them into a fresh directory and runs your{' '}
            <strong>entrypoint</strong> (default <code>python main.py</code>).
            Your script writes results to <code>output.json</code> in the same
            directory. That's the entire contract.
          </p>

          <h4 style={{ margin: '12px 0 4px', color: '#1e3a5f' }}>
            Minimal example workspace
          </h4>
          <p className={styles.muted}>
            Two files, dropped together into the upload box:
          </p>
          <pre
            style={{
              background: '#0f172a',
              color: '#e2e8f0',
              padding: 12,
              borderRadius: 6,
              fontSize: 12,
              overflowX: 'auto',
            }}
          >{`# main.py — a trivial LP solved with docplex
import json, time
from docplex.mp.model import Model

# Load any data files you uploaded alongside this script.
with open("inputs.json") as f:
    inputs = json.load(f)

start = time.time()
m = Model(name="example")
x = m.continuous_var_list(len(inputs["coeffs"]), lb=0, ub=10)
m.maximize(m.sum(c * xi for c, xi in zip(inputs["coeffs"], x)))
m.add_constraint(m.sum(x) <= inputs["budget"])
sol = m.solve()

output = {
    "status": "optimal" if sol else "infeasible",
    "objective": float(sol.objective_value) if sol else None,
    "solve_seconds": round(time.time() - start, 3),
    "n_vars": m.number_of_variables,
    "n_constraints": m.number_of_constraints,
    "tables": {
        "allocation": [
            {"i": i, "value": round(float(sol.get_value(xi)), 4)}
            for i, xi in enumerate(x)
        ] if sol else []
    },
}
with open("output.json", "w") as f:
    json.dump(output, f)
`}</pre>

          <p className={styles.muted} style={{ marginTop: 8 }}>
            And the matching <code>inputs.json</code> in the same workspace:
          </p>
          <pre
            style={{
              background: '#0f172a',
              color: '#e2e8f0',
              padding: 12,
              borderRadius: 6,
              fontSize: 12,
              overflowX: 'auto',
            }}
          >{`{ "coeffs": [3, 1, 2, 5, 4], "budget": 20 }`}</pre>

          <h4 style={{ margin: '12px 0 4px', color: '#1e3a5f' }}>
            output.json schema (all fields optional)
          </h4>
          <ul className={styles.muted} style={{ marginLeft: 22 }}>
            <li>
              <code>status</code>: <code>"optimal"</code> or <code>"infeasible"</code>.
              Defaults to optimal if absent.
            </li>
            <li>
              Any other top-level scalar keys (numbers, strings) render as
              summary cards on the Run tab.
            </li>
            <li>
              <code>tables</code>: <code>{'{ name: [{col: val, …}, …] }'}</code>{' '}
              — each entry renders as a table on the Run tab.
            </li>
          </ul>

          <h4 style={{ margin: '12px 0 4px', color: '#1e3a5f' }}>Notes</h4>
          <ul className={styles.muted} style={{ marginLeft: 22 }}>
            <li>
              The CPLEX engine + the <code>CPLEX_STUDIO_KEY</code> live on the VM —
              you don't need them in your workspace.
            </li>
            <li>
              Python packages installed by default: <code>cplex</code>,{' '}
              <code>docplex</code>, <code>pandas</code>, <code>numpy</code>. Ping{' '}
              if you need anything else system-wide.
            </li>
            <li>
              Workspace cap: 200 MB / 100 files. The runner mounts a fresh
              tmpfs working dir per run.
            </li>
            <li>
              Set a custom entrypoint (e.g. <code>bash run.sh</code>) when uploading
              if <code>python main.py</code> doesn't fit your model.
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Models tab
// ---------------------------------------------------------------------------

function ModelsPanel() {
  const [models, setModels] = useState<CplexModel[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<CplexModelDetail | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setModels(await listModels(includeArchived));
    } catch (e: any) {
      setErr(e?.response?.data?.detail ?? e?.message ?? 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div>
      <div className={styles.section}>
        <h3>Registered models</h3>
        <div style={{ marginBottom: 12 }}>
          <button className={styles.btn} onClick={() => setShowCreate(true)}>
            + New model
          </button>
          <label style={{ marginLeft: 16, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />{' '}
            Show archived
          </label>
        </div>

        {loading && <p className={styles.muted}>Loading…</p>}
        {err && <p className={styles.error}>{err}</p>}
        {!loading && !err && models.length === 0 && (
          <p className={styles.muted}>No models yet. Create one to get started.</p>
        )}

        {models.map((m) => (
          <ModelCard
            key={m.id}
            model={m}
            onChanged={refresh}
            onOpenDetail={async () => setEditing(await getModel(m.id))}
          />
        ))}
      </div>

      {showCreate && (
        <CreateModelDialog
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await refresh();
          }}
        />
      )}

      {editing && (
        <ModelDetailDialog
          detail={editing}
          onClose={() => setEditing(null)}
          onChanged={async () => {
            setEditing(await getModel(editing.id));
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function ModelCard({
  model,
  onChanged,
  onOpenDetail,
}: {
  model: CplexModel;
  onChanged: () => Promise<void>;
  onOpenDetail: () => Promise<void>;
}) {
  const isArchived = model.archived_at !== null;
  return (
    <div className={styles.modelCard}>
      <div className={styles.modelCardLeft}>
        <div className={styles.nameLine}>
          <strong>{model.name}</strong>
          {model.is_active && <span className={`${styles.badge} ${styles.active}`}>active</span>}
          <span className={`${styles.badge} ${styles.version}`}>
            v{model.current_version}
          </span>
          {isArchived && (
            <span className={`${styles.badge} ${styles.version}`}>archived</span>
          )}
        </div>
        {model.notes && <div className={styles.notes}>{model.notes}</div>}
        <div className={styles.meta}>
          Last updated {formatTime(model.updated_at)}
        </div>
      </div>
      <div className={styles.modelCardRight}>
        <button className={`${styles.btn} ${styles.small}`} onClick={onOpenDetail}>
          Open
        </button>
        {!isArchived && !model.is_active && model.current_version > 0 && (
          <button
            className={`${styles.btn} ${styles.small} ${styles.secondary}`}
            onClick={async () => {
              await updateModel(model.id, { is_active: true });
              await onChanged();
            }}
          >
            Set active
          </button>
        )}
        {!isArchived && (
          <button
            className={`${styles.btn} ${styles.small} ${styles.danger}`}
            onClick={async () => {
              if (!window.confirm(`Archive "${model.name}"?`)) return;
              await archiveModel(model.id);
              await onChanged();
            }}
          >
            Archive
          </button>
        )}
      </div>
    </div>
  );
}

function CreateModelDialog({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [entrypoint, setEntrypoint] = useState('python main.py');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className={styles.section}>
      <h3>New model</h3>
      <div className={styles.fieldRow}>
        <label>Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Production Allocation"
        />
      </div>
      <div className={styles.fieldRow}>
        <label>Notes (markdown)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What does this model do? Inputs? Constraints to watch?"
        />
      </div>
      <div className={styles.fieldRow}>
        <label>Entrypoint</label>
        <input
          type="text"
          value={entrypoint}
          onChange={(e) => setEntrypoint(e.target.value)}
        />
      </div>
      <div className={styles.fieldRow}>
        <label>Workspace files</label>
        <FilePicker files={files} setFiles={setFiles} />
      </div>
      {err && <p className={styles.error}>{err}</p>}
      <button
        className={styles.btn}
        disabled={!name.trim() || files.length === 0 || submitting}
        onClick={async () => {
          setSubmitting(true);
          setErr(null);
          try {
            await createModel({ name: name.trim(), notes, entrypoint, files });
            await onCreated();
          } catch (e: any) {
            setErr(e?.response?.data?.detail ?? e?.message ?? 'Failed.');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {submitting ? 'Uploading…' : 'Create model'}
      </button>
      <button
        className={`${styles.btn} ${styles.secondary}`}
        style={{ marginLeft: 8 }}
        onClick={onCancel}
        disabled={submitting}
      >
        Cancel
      </button>
    </div>
  );
}

function ModelDetailDialog({
  detail,
  onClose,
  onChanged,
}: {
  detail: CplexModelDetail;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [notes, setNotes] = useState(detail.notes ?? '');
  const [name, setName] = useState(detail.name);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newNotes, setNewNotes] = useState('');
  const [newEntrypoint, setNewEntrypoint] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Notebook-mode upload state (PJ's path — single .ipynb or .py).
  const [notebookFile, setNotebookFile] = useState<File | null>(null);
  const [notebookNotes, setNotebookNotes] = useState('');
  const [notebookBusy, setNotebookBusy] = useState(false);
  const [notebookErr, setNotebookErr] = useState<string | null>(null);
  const [notebookOk, setNotebookOk] = useState<CplexModelVersion | null>(null);
  const [guideMd, setGuideMd] = useState<string | null>(null);
  const [guideBusy, setGuideBusy] = useState(false);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const latest = detail.versions[0]; // versions sorted desc by api

  return (
    <div
      className={styles.modalBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modalCard}>
        <button
          className={styles.modalClose}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      <h3>
        {detail.name} <span className={styles.muted}>· v{detail.current_version}</span>
      </h3>

      <div className={styles.fieldRow}>
        <label>Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className={styles.fieldRow}>
        <label>Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <button
        className={`${styles.btn} ${styles.small}`}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            await updateModel(detail.id, { name: name.trim(), notes });
            await onChanged();
          } catch (e: any) {
            setErr(e?.response?.data?.detail ?? e?.message ?? 'Failed.');
          } finally {
            setBusy(false);
          }
        }}
      >
        Save
      </button>
      <button
        className={`${styles.btn} ${styles.small} ${styles.secondary}`}
        style={{ marginLeft: 8 }}
        onClick={onClose}
      >
        Close
      </button>

      <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #e2e8f0' }} />

      <h3>
        Upload notebook (single file){' '}
        <button
          className={`${styles.btn} ${styles.small} ${styles.secondary}`}
          style={{ marginLeft: 12, fontSize: 12 }}
          disabled={guideBusy}
          onClick={async () => {
            if (guideMd) {
              setGuideMd(null);  // toggle off
              return;
            }
            setGuideBusy(true);
            try {
              const md = await getNotebookUploadGuide();
              setGuideMd(md);
            } catch {
              setGuideMd(
                '# Guide unavailable\n\nFailed to load the notebook upload guide.',
              );
            } finally {
              setGuideBusy(false);
            }
          }}
        >
          {guideMd ? 'Hide guide' : guideBusy ? 'Loading…' : '📖 Guide'}
        </button>
      </h3>
      {guideMd && (
        <pre
          style={{
            background: '#f7f8fa',
            border: '1px solid #e2e8f0',
            borderRadius: 4,
            padding: 12,
            fontSize: 12,
            lineHeight: 1.5,
            maxHeight: 360,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          {guideMd}
        </pre>
      )}
      <p className={styles.muted} style={{ marginTop: -4, fontSize: 13 }}>
        Drop a single <code>.ipynb</code> or <code>.py</code>. The server
        converts the notebook, wraps it with the standard{' '}
        <code>output.json</code> emitter, and carries forward the input
        CSVs from v{detail.current_version}. No tarball assembly required.
      </p>
      {detail.current_version === 0 && (
        <p className={styles.error} style={{ marginTop: 0 }}>
          This model has no prior workspace to harvest CSVs from. Upload a
          full tarball first via the "Upload new version" section below.
        </p>
      )}
      <div className={styles.fieldRow}>
        <label>Notebook (.ipynb) or script (.py)</label>
        <input
          type="file"
          accept=".ipynb,.py,application/x-ipynb+json,text/x-python"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setNotebookFile(f);
            setNotebookErr(null);
            setNotebookOk(null);
          }}
        />
        {notebookFile && (
          <div className={styles.meta} style={{ marginTop: 4 }}>
            Selected: <code>{notebookFile.name}</code>{' '}
            ({formatBytes(notebookFile.size)})
          </div>
        )}
      </div>
      <div className={styles.fieldRow}>
        <label>Notes (optional)</label>
        <textarea
          value={notebookNotes}
          onChange={(e) => setNotebookNotes(e.target.value)}
          placeholder="e.g. 'Fixed Order Required By date coercion'"
        />
      </div>
      {notebookErr && <p className={styles.error}>{notebookErr}</p>}
      {notebookOk && (
        <div
          className={styles.modelCard}
          style={{ marginBottom: 12, borderColor: '#16a34a' }}
        >
          <div className={styles.modelCardLeft}>
            <div className={styles.nameLine}>
              <strong>✓ Uploaded v{notebookOk.version}</strong>
              <span className={styles.meta}>
                {formatTime(notebookOk.uploaded_at)}
              </span>
            </div>
            <div className={styles.meta}>
              {notebookOk.file_manifest.length} files in the workspace ·
              entrypoint: <code>{notebookOk.entrypoint}</code>
            </div>
          </div>
        </div>
      )}
      <button
        className={styles.btn}
        disabled={!notebookFile || notebookBusy || detail.current_version === 0}
        onClick={async () => {
          if (!notebookFile) return;
          setNotebookBusy(true);
          setNotebookErr(null);
          setNotebookOk(null);
          try {
            const created = await uploadNotebookVersion(detail.id, {
              notebook: notebookFile,
              notes: notebookNotes || undefined,
            });
            setNotebookOk(created);
            setNotebookFile(null);
            setNotebookNotes('');
            await onChanged();
          } catch (e: any) {
            setNotebookErr(
              e?.response?.data?.detail ?? e?.message ?? 'Upload failed.',
            );
          } finally {
            setNotebookBusy(false);
          }
        }}
      >
        {notebookBusy
          ? 'Uploading…'
          : `Upload notebook as v${detail.current_version + 1}`}
      </button>

      <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #e2e8f0' }} />

      <h3>Upload new version</h3>
      <div className={styles.fieldRow}>
        <label>Notes for this version</label>
        <textarea value={newNotes} onChange={(e) => setNewNotes(e.target.value)} />
      </div>
      <div className={styles.fieldRow}>
        <label>Entrypoint (blank = same as current)</label>
        <input
          type="text"
          value={newEntrypoint}
          onChange={(e) => setNewEntrypoint(e.target.value)}
          placeholder={latest?.entrypoint ?? 'python main.py'}
        />
      </div>
      <div className={styles.fieldRow}>
        <label>Files</label>
        <FilePicker files={newFiles} setFiles={setNewFiles} />
      </div>
      {err && <p className={styles.error}>{err}</p>}
      <button
        className={styles.btn}
        disabled={newFiles.length === 0 || busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            await uploadVersion(detail.id, {
              notes: newNotes || undefined,
              entrypoint: newEntrypoint || undefined,
              files: newFiles,
            });
            setNewFiles([]);
            setNewNotes('');
            setNewEntrypoint('');
            await onChanged();
          } catch (e: any) {
            setErr(e?.response?.data?.detail ?? e?.message ?? 'Failed.');
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? 'Uploading…' : `Upload v${detail.current_version + 1}`}
      </button>

      <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #e2e8f0' }} />

      <h3>Version history</h3>
      {detail.versions.map((v) => (
        <VersionCard
          key={v.id}
          version={v}
          isActive={v.version === detail.current_version}
          modelId={detail.id}
        />
      ))}

      <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #e2e8f0' }} />

      <CplexSourcesPanel modelId={detail.id} />

      <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #e2e8f0' }} />

      <h3>Runs for this model</h3>
      <ModelRunsList modelId={detail.id} />
      </div>
    </div>
  );
}

function VersionCard({
  version,
  isActive,
  modelId,
}: {
  version: CplexModelVersion;
  isActive: boolean;
  modelId: string;
}) {
  const [openFile, setOpenFile] = useState<WorkspaceFileContent | null>(null);
  const [loadingFile, setLoadingFile] = useState<string | null>(null);
  const [fileErr, setFileErr] = useState<string | null>(null);

  return (
    <div className={styles.modelCard}>
      <div className={styles.modelCardLeft}>
        <div className={styles.nameLine}>
          <strong>v{version.version}</strong>
          {isActive && <span className={`${styles.badge} ${styles.active}`}>active</span>}
          <span className={styles.meta}>{formatTime(version.uploaded_at)}</span>
        </div>
        <div className={styles.meta}>
          Entrypoint: <code>{version.entrypoint}</code>
        </div>
        <div style={{ marginTop: 6 }}>
          {version.file_manifest.map((f) => (
            <button
              key={f.name}
              className={styles.filePill}
              style={{ border: 0, cursor: 'pointer' }}
              title="Click to preview file contents"
              onClick={async () => {
                setLoadingFile(f.name);
                setFileErr(null);
                try {
                  setOpenFile(await getWorkspaceFile(modelId, version.version, f.name));
                } catch (e: any) {
                  setFileErr(e?.response?.data?.detail ?? e?.message ?? 'Failed.');
                } finally {
                  setLoadingFile(null);
                }
              }}
            >
              {f.name} <span className={styles.muted}>({formatBytes(f.size_bytes)})</span>
              {loadingFile === f.name && <span className={styles.muted}> · loading…</span>}
            </button>
          ))}
        </div>
        {fileErr && <p className={styles.error}>{fileErr}</p>}
        {version.notes && <div className={styles.notes} style={{ marginTop: 6 }}>{version.notes}</div>}
        <div style={{ marginTop: 8 }}>
          <button
            className={`${styles.btn} ${styles.small} ${styles.secondary}`}
            onClick={async () => {
              try {
                const { download_url, filename } = await getWorkspaceDownloadUrl(
                  modelId,
                  version.version,
                );
                const a = document.createElement('a');
                a.href = download_url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
              } catch (e: any) {
                setFileErr(e?.response?.data?.detail ?? e?.message ?? 'Failed.');
              }
            }}
          >
            ↓ Download workspace
          </button>
        </div>
      </div>
      {openFile && (
        <FilePreviewModal file={openFile} onClose={() => setOpenFile(null)} />
      )}
    </div>
  );
}

function FilePreviewModal({
  file,
  onClose,
}: {
  file: WorkspaceFileContent;
  onClose: () => void;
}) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);
  return (
    <div
      className={styles.modalBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{ zIndex: 1100 }}
    >
      <div className={styles.modalCard} style={{ maxWidth: 900 }}>
        <button className={styles.modalClose} onClick={onClose} aria-label="Close">×</button>
        <h3>{file.name}</h3>
        <div className={styles.meta}>
          {formatBytes(file.size_bytes)} · {file.encoding}
          {file.truncated && (
            <span className={styles.error}> · truncated to first 1 MB</span>
          )}
        </div>
        {file.encoding === 'utf-8' ? (
          <pre style={{
            background: '#0f172a',
            color: '#e2e8f0',
            padding: 12,
            borderRadius: 6,
            fontSize: 12,
            overflow: 'auto',
            maxHeight: '60vh',
            marginTop: 10,
          }}>{file.content}</pre>
        ) : (
          <p className={styles.muted} style={{ marginTop: 10 }}>
            Binary file (base64 payload, {file.content.length} chars). Use the
            Download workspace button to inspect locally.
          </p>
        )}
      </div>
    </div>
  );
}

function ModelRunsList({ modelId }: { modelId: string }) {
  const [rows, setRows] = useState<CplexRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listRuns(modelId, 25, 0);
        if (!cancelled) setRows(res.items);
      } catch (e: any) {
        if (!cancelled) setErr(e?.response?.data?.detail ?? e?.message ?? 'Failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  if (loading) return <p className={styles.muted}>Loading…</p>;
  if (err) return <p className={styles.error}>{err}</p>;
  if (rows.length === 0) return <p className={styles.muted}>No runs for this model yet.</p>;
  return (
    <div>
      {rows.map((r) => (
        <div key={r.id} className={styles.runRow}>
          <div className="time">{formatTime(r.created_at)}</div>
          <div className="state">
            <span className={`${styles.badge} ${styles[r.state]}`}>
              {stateLabel(r.state)}
            </span>
          </div>
          <div className="obj">
            {r.objective != null
              ? `obj = ${r.objective.toLocaleString()}`
              : r.error_message ?? '—'}
            {r.solve_seconds != null && (
              <span className={styles.muted}> · {r.solve_seconds.toFixed(2)}s</span>
            )}
            <span className={styles.muted}> · v{r.model_version}</span>
          </div>
          <RunDownloadButton runId={r.id} disabled={!TERMINAL_STATES.has(r.state)} />
        </div>
      ))}
    </div>
  );
}

function FilePicker({
  files,
  setFiles,
}: {
  files: File[];
  setFiles: (f: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const next = [...files];
    for (let i = 0; i < incoming.length; i++) {
      const f = incoming.item(i);
      if (f && !next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    setFiles(next);
  };

  return (
    <div>
      <div
        className={`${styles.uploadBox} ${drag ? styles.drag : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <strong>Drop files here</strong> or click to browse
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>
      {files.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {files.map((f) => (
            <span key={f.name} className={styles.filePill}>
              {f.name} <span className={styles.muted}>({formatBytes(f.size)})</span>
              <button
                onClick={() => setFiles(files.filter((x) => x !== f))}
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Run tab
// ---------------------------------------------------------------------------

function RunPanel() {
  const [models, setModels] = useState<CplexModel[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [run, setRun] = useState<CplexRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const list = await listModels(false);
      setModels(list);
      const def = list.find((m) => m.is_active) ?? list[0];
      if (def) setSelectedId(def.id);
    })();
  }, []);

  // Poll the active run until terminal
  useEffect(() => {
    if (!run || isTerminalState(run.state)) return;
    const handle = setInterval(async () => {
      try {
        const next = await getRun(run.id);
        setRun(next);
      } catch (e) {
        // surface error but don't kill polling; user can refresh.
        console.error('cplex poll failed', e);
      }
    }, 2000);
    return () => clearInterval(handle);
  }, [run]);

  const selected = useMemo(
    () => models.find((m) => m.id === selectedId) ?? null,
    [models, selectedId],
  );

  return (
    <div>
      <div className={styles.section}>
        <h3>Model</h3>
        <select
          className={styles.fieldRow}
          style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1' }}
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {models.length === 0 && <option value="">— no models registered —</option>}
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} — v{m.current_version}
              {m.is_active ? ' (active)' : ''}
            </option>
          ))}
        </select>
        {selected && (
          <p className={styles.muted} style={{ marginTop: 6 }}>
            {selected.notes ?? 'No notes for this model.'}
          </p>
        )}
      </div>

      <div className={styles.section}>
        <h3>Run</h3>
        <button
          className={styles.btn}
          disabled={
            !selected ||
            selected.current_version === 0 ||
            starting ||
            (run !== null && !isTerminalState(run.state))
          }
          onClick={async () => {
            if (!selected) return;
            setStarting(true);
            setErr(null);
            try {
              const r = await startRun(selected.id);
              setRun(r);
            } catch (e: any) {
              setErr(e?.response?.data?.detail ?? e?.message ?? 'Failed to start.');
            } finally {
              setStarting(false);
            }
          }}
        >
          {starting ? 'Starting…' : '▶ Run model'}
        </button>

        {run && <RunStatusBar run={run} />}
        {err && <p className={styles.error}>{err}</p>}
      </div>

      {run && isTerminalState(run.state) && run.state !== 'error' && (
        <ResultsSection run={run} />
      )}
      {run && run.state === 'error' && (
        <div className={`${styles.statusBar} ${styles.err}`}>
          <strong>Error.</strong> {run.error_message ?? 'No detail.'}
        </div>
      )}
    </div>
  );
}

function RunStatusBar({ run }: { run: CplexRun }) {
  if (run.state === 'optimal' || run.state === 'infeasible') {
    const obj = (run.result_summary?.objective as number | undefined) ?? undefined;
    const t = (run.result_summary?.solve_seconds as number | undefined) ?? undefined;
    return (
      <div className={`${styles.statusBar} ${styles.ok}`}>
        <strong>{stateLabel(run.state)}.</strong>{' '}
        {obj !== undefined && <>objective <strong>{obj.toLocaleString()}</strong> · </>}
        {t !== undefined && <>{t.toFixed(2)} s</>}
      </div>
    );
  }
  if (run.state === 'error') return null;
  return (
    <div className={`${styles.statusBar} ${styles.running}`}>
      <strong>{stateLabel(run.state)}…</strong>
    </div>
  );
}

function ResultsSection({ run }: { run: CplexRun }) {
  const summary = run.result_summary ?? {};
  const tables = run.result_tables ?? {};
  return (
    <div className={styles.section}>
      <h3>Results</h3>
      <div className={styles.summaryGrid}>
        {Object.entries(summary).map(([k, v]) => (
          <div key={k}>
            <span className="label">{k}</span>
            <span className="val">{String(v)}</span>
          </div>
        ))}
      </div>
      {Object.entries(tables).map(([name, rows]) => (
        <div key={name} style={{ marginTop: 12 }}>
          <h4 style={{ margin: 0, color: '#1e3a5f' }}>{name}</h4>
          <ResultTable rows={rows} />
        </div>
      ))}
    </div>
  );
}

function ResultTable({ rows }: { rows: unknown }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return <p className={styles.muted}>No rows.</p>;
  }
  const first = rows[0];
  if (typeof first !== 'object' || first === null) {
    return (
      <pre className={styles.muted}>{JSON.stringify(rows, null, 2)}</pre>
    );
  }
  const cols = Object.keys(first as Record<string, unknown>);
  return (
    <table className={styles.resultsTable}>
      <thead>
        <tr>
          {cols.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 200).map((r, idx) => {
          const row = r as Record<string, unknown>;
          return (
            <tr key={idx}>
              {cols.map((c) => (
                <td key={c}>{String(row[c] ?? '')}</td>
              ))}
            </tr>
          );
        })}
        {rows.length > 200 && (
          <tr>
            <td colSpan={cols.length} className={styles.muted}>
              … {rows.length - 200} more rows omitted
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// RunDownloadButton — fetches /admin/cplex/runs/{id}/download and triggers a
// browser save. Disabled while a run is still solving (no useful zip yet).
// ---------------------------------------------------------------------------

function RunDownloadButton({
  runId,
  disabled,
}: {
  runId: string;
  disabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onClick = async () => {
    setBusy(true);
    setErr(null);
    try {
      await downloadRunArchive(runId);
    } catch (e: unknown) {
      const obj = e as { message?: unknown };
      setErr(typeof obj?.message === 'string' ? obj.message : 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || busy}
        title={
          disabled
            ? 'Download available once the run is in a terminal state.'
            : 'Download a zip of this run’s inputs (workspace + CSVs) and outputs.'
        }
        className={`${styles.btn} ${styles.small} ${styles.secondary}`}
      >
        {busy ? 'Zipping…' : '⬇ Download zip'}
      </button>
      {err && <span className={styles.error}>{err}</span>}
    </div>
  );
}


function HistoryPanel() {
  const [rows, setRows] = useState<CplexRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await listRuns(undefined, 50, 0);
      setRows(res.items);
    } catch (e: any) {
      setErr(e?.response?.data?.detail ?? e?.message ?? 'Failed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Auto-refresh every 4s while any non-terminal row is present.
  useEffect(() => {
    const hasActive = rows.some((r) => !TERMINAL_STATES.has(r.state));
    if (!hasActive) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [rows, refresh]);

  return (
    <div className={styles.section}>
      <h3>Recent runs</h3>
      {loading && <p className={styles.muted}>Loading…</p>}
      {err && <p className={styles.error}>{err}</p>}
      {!loading && !err && rows.length === 0 && (
        <p className={styles.muted}>No runs yet.</p>
      )}
      {rows.map((r) => (
        <div key={r.id} className={styles.runRow}>
          <div className="time">{formatTime(r.created_at)}</div>
          <div className="state">
            <span className={`${styles.badge} ${styles[r.state]}`}>
              {stateLabel(r.state)}
            </span>
          </div>
          <div className="obj">
            {r.objective != null
              ? `obj = ${r.objective.toLocaleString()}`
              : r.error_message ?? '—'}
            {r.solve_seconds != null && (
              <span className={styles.muted}> · {r.solve_seconds.toFixed(2)}s</span>
            )}
            <span className={styles.muted}> · v{r.model_version}</span>
          </div>
          <RunDownloadButton runId={r.id} disabled={!TERMINAL_STATES.has(r.state)} />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sankey tab — supply-chain flow visualization from a run's result_tables.
// ---------------------------------------------------------------------------

function extractErrMessage(e: unknown): string {
  if (e && typeof e === 'object') {
    const obj = e as {
      response?: { data?: { detail?: unknown } };
      message?: unknown;
    };
    const detail = obj.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (typeof obj.message === 'string') return obj.message;
  }
  return 'Failed.';
}

function SankeyPanel() {
  const [runs, setRuns] = useState<CplexRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [run, setRun] = useState<CplexRun | null>(null);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [loadingRun, setLoadingRun] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadingRuns(true);
      try {
        const res = await listRuns(undefined, 25, 0);
        if (cancelled) return;
        const completed = res.items.filter((r) =>
          TERMINAL_STATES.has(r.state),
        );
        setRuns(completed);
      } catch (e: unknown) {
        if (!cancelled) {
          setErr(extractErrMessage(e));
        }
      } finally {
        if (!cancelled) setLoadingRuns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Default to the most recent completed run once the list arrives.
  useEffect(() => {
    if (!selectedRunId && runs.length > 0) {
      setSelectedRunId(runs[0].id);
    }
  }, [runs, selectedRunId]);

  useEffect(() => {
    if (!selectedRunId) return;
    let cancelled = false;
    void (async () => {
      setLoadingRun(true);
      setErr(null);
      try {
        const r = await getRun(selectedRunId);
        if (!cancelled) setRun(r);
      } catch (e: unknown) {
        if (!cancelled) {
          setErr(extractErrMessage(e));
        }
      } finally {
        if (!cancelled) setLoadingRun(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  const sankey = useMemo<SankeyPayload | null>(() => {
    const raw = run?.result_tables?.sankey ?? run?.result_summary?.sankey;
    return (raw as SankeyPayload | undefined) ?? null;
  }, [run]);

  const mppRows = useMemo<MPPRow[] | null>(() => {
    const raw = run?.result_tables?.master_production_plan;
    if (!Array.isArray(raw)) return null;
    return raw as unknown as MPPRow[];
  }, [run]);

  return (
    <div className={styles.section}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h3>Supply-chain flow Sankey</h3>
        {selectedRunId && (
          <Link
            to={`/cplex/sankey/${selectedRunId}`}
            className={styles.muted}
            style={{ fontSize: 12 }}
          >
            ↗ Open full-bleed view
          </Link>
        )}
      </div>
      <p className={styles.muted}>
        Renders the aggregated flow JSON computed on the CPLEX VM for the
        selected run. Click a node or link to highlight every connected
        flow; toggle fullscreen for a wider canvas.
      </p>

      {loadingRuns && <p className={styles.muted}>Loading runs…</p>}
      {!loadingRuns && runs.length === 0 && (
        <p className={styles.muted}>
          No completed runs yet — start a run on the Run tab.
        </p>
      )}

      {runs.length > 0 && (
        <label className={styles.muted} style={{ display: 'block', margin: '8px 0' }}>
          Run:&nbsp;
          <select
            value={selectedRunId ?? ''}
            onChange={(e) => setSelectedRunId(e.target.value || null)}
          >
            {runs.map((r) => (
              <option key={r.id} value={r.id}>
                {formatTime(r.created_at)} ·{' '}
                {r.model_name ?? 'unknown model'} · v{r.model_version} ·{' '}
                {stateLabel(r.state)}
                {r.solve_seconds != null
                  ? ` · ${r.solve_seconds.toFixed(1)}s`
                  : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      {err && <p className={styles.error}>{err}</p>}
      {loadingRun && <p className={styles.muted}>Loading run…</p>}

      {!loadingRun && run && !sankey && !mppRows && (
        <p className={styles.muted}>
          This run has no Sankey payload — it likely came from an older
          model version that pre-dates the embedded flow output.
        </p>
      )}

      {/* Prefer the daily dashboard when master_production_plan is available
        (richer per-day filter + side panels + click-to-detail). Fall back to
        the bare SankeyChart for older runs that only have the aggregated
        sankey payload. */}
      {mppRows ? (
        <DailyDashboard rows={mppRows} fallbackPayload={sankey} />
      ) : (
        sankey && <SankeyChart payload={sankey} />
      )}
    </div>
  );
}

export default CplexAdminPage;
